import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, RefreshCw, Wrench, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const inr = (n) => '₹' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-IN');

// Live voucher ⇄ ledger integrity: badge in the Vouchers header + repair panel.
export const IntegrityBadge = ({ isAdmin, refreshKey }) => {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/vouchers/integrity`, { withCredentials: true }); setReport(r.data); }
    catch { /* non-manager roles get 403 — badge simply hides */ }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const repair = async (ids) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vouchers/integrity/repair`, ids ? { voucher_ids: ids } : { all: true }, { withCredentials: true });
      toast.success(`Repaired ${r.data.repaired} · ${r.data.remaining} mismatch(es) left`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Repair failed'); }
    finally { setBusy(false); }
  };

  const createBill = async (vid) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vouchers/integrity/create-bill/${vid}`, {}, { withCredentials: true });
      toast.success(`Bill ${r.data.invoice_number || ''} created — now in the ledger`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create bill'); }
    finally { setBusy(false); }
  };

  const repairOpening = async (clientId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vouchers/integrity/repair-opening`, clientId ? { client_id: clientId } : { all: true }, { withCredentials: true });
      toast.success(`Opening balance repaired for ${r.data.repaired} client(s) · ${r.data.remaining} left`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Repair failed'); }
    finally { setBusy(false); }
  };

  if (!report) return null;
  const rows = [...report.sales, ...report.purchases];
  const opening = report.opening || [];
  const ok = report.count === 0;
  const canRepairAll = isAdmin && (rows.length > 0 || opening.length > 0);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="integrity-badge"
        className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition-colors ${ok ? 'border-emerald-700/50 text-emerald-300 bg-emerald-500/10' : 'border-red-700/60 text-red-300 bg-red-500/10 hover:bg-red-500/20'}`}>
        {ok ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
        {ok ? 'Ledger in sync' : `${report.count} mismatch${report.count > 1 ? 'es' : ''}`}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="integrity-panel">
          <div className="bg-[#0F0F11] border border-zinc-700 rounded-2xl w-full max-w-4xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <div>
                <div className="text-zinc-100 font-semibold flex items-center gap-2">{ok ? <ShieldCheck size={16} className="text-emerald-400" /> : <ShieldAlert size={16} className="text-red-400" />} Voucher ⇄ ledger integrity</div>
                <div className="text-zinc-500 text-[11px] mt-0.5">Checked {new Date(report.checked_at).toLocaleString('en-IN')} · sales: voucher wins · purchases: voucher re-mirrors the bill · opening balance: live receipts win · every edit re-syncs automatically</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={load} className="text-zinc-400 hover:text-zinc-100 p-1.5" title="Re-check" data-testid="integrity-recheck"><RefreshCw size={14} /></button>
                {canRepairAll && (
                  <button onClick={async () => { if (rows.length) await repair(null); if (opening.length) await repairOpening(null); }} disabled={busy} data-testid="integrity-repair-all"
                    className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
                    <Wrench size={12} /> Repair all ({report.count})
                  </button>
                )}
                <button onClick={() => setOpen(false)} data-testid="integrity-close"><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {ok ? (
                <div className="p-10 text-center text-emerald-300 text-sm">Every sales & purchase voucher matches its bill and the ledger, and every opening balance agrees with its receipts.</div>
              ) : rows.length > 0 && (
                <table className="w-full text-xs">
                  <thead className="bg-zinc-950 text-zinc-500 text-[10px] uppercase sticky top-0">
                    <tr><th className="text-left px-4 py-2">Voucher</th><th className="text-left px-3 py-2">Party</th><th className="text-left px-3 py-2">Bill / ledger ref</th>
                      <th className="text-right px-3 py-2">Voucher</th><th className="text-right px-3 py-2">Ledger</th><th className="text-left px-3 py-2">What differs</th><th className="px-3 py-2" /></tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.voucher_id} className="border-t border-zinc-800/60 text-zinc-300" data-testid={`integrity-row-${r.voucher_id}`}>
                        <td className="px-4 py-2 font-mono text-amber-300">{r.voucher_number}<div className="text-[10px] text-zinc-500">{r.side} · {r.date}</div></td>
                        <td className="px-3 py-2">{r.party}</td>
                        <td className="px-3 py-2 font-mono">{r.bill_ref || r.bill_id}</td>
                        <td className="px-3 py-2 text-right font-mono">{inr(r.voucher_amount)}</td>
                        <td className={`px-3 py-2 text-right font-mono ${Math.abs(r.voucher_amount - r.bill_amount) > 0.009 ? 'text-red-300' : ''}`}>{inr(r.bill_amount)}</td>
                        <td className="px-3 py-2 text-[11px]">{r.diffs.map(d => <div key={d.field}><span className="text-zinc-500">{d.field}:</span> {String(d.voucher)} <span className="text-zinc-600">vs</span> {String(d.bill)}</div>)}</td>
                        <td className="px-3 py-2 text-right">
                          {isAdmin && <button onClick={() => repair([r.voucher_id])} disabled={busy} data-testid={`integrity-repair-${r.voucher_id}`}
                            className="text-[11px] border border-zinc-700 hover:border-amber-500 text-zinc-200 px-2 py-1 rounded-lg disabled:opacity-50">Repair</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {opening.length > 0 && (
                <div className="px-5 py-3 border-t border-zinc-800" data-testid="integrity-opening">
                  <div className="text-[11px] text-zinc-400 mb-2">{opening.length} client(s) whose opening balance disagrees with their receipts — deleted receipts still counted, or receipts with no slice against the B/F:</div>
                  <table className="w-full text-xs">
                    <thead className="text-zinc-500 text-[10px] uppercase"><tr><th className="text-left px-2 py-1">Client</th><th className="text-right px-2 py-1">Opening</th><th className="text-right px-2 py-1">Stored settled</th><th className="text-right px-2 py-1">Live settled</th><th className="text-left px-2 py-1">Why</th><th /></tr></thead>
                    <tbody>
                      {opening.map(o => (
                        <tr key={o.client_id} className="border-t border-zinc-800/60 text-zinc-300" data-testid={`integrity-opening-${o.client_id}`}>
                          <td className="px-2 py-1.5">{o.client_name}<div className="text-[10px] text-zinc-500">{o.client_type}</div></td>
                          <td className="px-2 py-1.5 text-right font-mono">{inr(o.opening_balance)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-red-300">{inr(o.scalar)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{inr(o.valid_total)}</td>
                          <td className="px-2 py-1.5 text-[11px]">
                            {o.orphans.length > 0 && <div>{o.orphans.length} deleted receipt(s) still counted · {inr(o.orphan_total)}</div>}
                            {o.unrepresented.length > 0 && <div>{o.unrepresented.length} receipt(s) against B/F with no slice · {inr(o.unrepresented_total)} ({o.unrepresented.map(u => u.voucher_number).join(', ')})</div>}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {isAdmin && <button onClick={() => repairOpening(o.client_id)} disabled={busy} data-testid={`integrity-repair-opening-${o.client_id}`}
                              className="text-[11px] border border-zinc-700 hover:border-amber-500 text-zinc-200 px-2 py-1 rounded-lg disabled:opacity-50">Repair</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {report.unlinked?.length > 0 && (
                <div className="px-5 py-3 border-t border-zinc-800" data-testid="integrity-unlinked">
                  <div className="text-[11px] text-zinc-400 mb-2">{report.unlinked.length} active voucher(s) have no bill behind them — they are missing from the ledger until a bill is created:</div>
                  <div className="flex flex-wrap gap-2">
                    {report.unlinked.map(u => (
                      <div key={u.voucher_id} className="flex items-center gap-2 text-[11px] border border-zinc-800 rounded-lg px-2 py-1" data-testid={`integrity-unlinked-${u.voucher_id}`}>
                        <span className="font-mono text-amber-300">{u.voucher_number}</span><span className="text-zinc-500">{u.party}</span><span className="font-mono text-zinc-300">{inr(u.amount)}</span>
                        {isAdmin && u.side === 'sales' && (
                          <button onClick={() => createBill(u.voucher_id)} disabled={busy} data-testid={`integrity-create-bill-${u.voucher_id}`}
                            className="text-[10px] border border-zinc-700 hover:border-amber-500 text-zinc-200 px-1.5 py-0.5 rounded disabled:opacity-50">Create bill</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
